import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
// Common Components
import AppButton from "../../../common_components/AppButton";
import useToast from "../../../common_components/Toast/useToast";
import CommonLoader from "../../../common_components/CommonLoader";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
// Tab Components
import ClientInfoTab from "./ClientInfoTab";
import ContactsTab from "./ContactsTab";
import SettingsTab from "./SettingsTab";
import QuickBookTab from "./QuickBookTab";
import ChangeLogsTab from "./ChangeLogsTab";
import { fetchClientLogs, checkEmailExists, deleteContact, resetContactPassword, createClient, updateClient, fetchAllClientList, } from "../../../redux/clientDataSlice";
import { selectCanManageClients, selectCanInviteClients } from "../../../redux/authSlice";
import { ApiCall } from "../../../helper/axios";
import { CalendarDate } from "@internationalized/date";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { useOverlayHoverHandlers } from "../../../common_components/hoverOverlay/HoverOverlayProvider";
import { Box } from "@mui/material";
/* ================= INITIAL DATA ================= */
const initialStoreData = {
    name: "",
    type: [],
    is_main_account: null,
    main_account_id: null,
    status: "active",
    monday_calender_id: "",
    monday_board_id: "",
    start_date: "",
    termination_date: "",
    website_url: "",
    role_id: null,
    profile_pic: null,
};
const initialContact = {
    id: Date.now(),
    firstName: "",
    lastName: "",
    email: "",
    allowEmailNotification: false,
    status: "active",
};
const initialQuickbookConfig = {
    display_name: "",
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    internal_customer_notes: "",
    street_address_first: "",
    street_address_second: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    payment: [],
};
const initialSettings = {
    shopify_filter: [],
    combined_bench_mark: false,
    include_cpaIn_ad_testing: false,
    performance_tracking: {
        networks: {}
    },
    shopify_revenue_settings: {
        account_summary: "G-D+S+T",
        performance_tracker: "G-D+S+T",
        client_report: "G-D+S+T",
        divergence_report: "G-D+S+T",
        commission_tracker: "netsales",
    },
};
const initialBenchmarkData = {
    spend: { microdata: "", evergreen: "", promo1st: [{ targetMin: 0, targetMax: 0 }], promo2nd: "" },
    cpoc: { microdata: "", evergreen: "", promo1st: "", promo2nd: "" },
    octr: { microdata: "", evergreen: "", promo1st: "", promo2nd: "" },
    roas: { microdata: "", evergreen: "", promo1st: "", promo2nd: "" },
    cpa: { microdata: "", evergreen: "", promo1st: "", promo2nd: "" },
};
// Benchmark values are stored as numbers in the DB but edited through text
// inputs, so the form state holds strings. `toFormString` coerces a stored
// number/null into the string the inputs expect (null/undefined -> "").
const toFormString = (v) => v === null || v === undefined ? "" : String(v);
// Coerce a stored benchmarks object into the form's string-based shape.
// Also upgrades a legacy string spend.promo1st (e.g. "100 - 130") into the
// numeric range array shape so older records still render in the editor.
const normalizeBenchmarks = (benchmarks) => {
    if (!benchmarks)
        return initialBenchmarkData;
    const scalarGroup = (g = {}) => ({
        microdata: toFormString(g.microdata),
        evergreen: toFormString(g.evergreen),
        promo1st: toFormString(g.promo1st),
        promo2nd: toFormString(g.promo2nd),
    });
    const p1 = benchmarks.spend?.promo1st;
    let targetMin = 0;
    let targetMax = 0;
    if (Array.isArray(p1)) {
        // Legacy shape: array of objects [{ targetMin, targetMax }].
        targetMin = p1[0]?.targetMin ?? 0;
        targetMax = p1[0]?.targetMax ?? 0;
    }
    else if (p1 && typeof p1 === "object") {
        // New shape: single object { targetMin, targetMax }.
        targetMin = p1.targetMin ?? 0;
        targetMax = p1.targetMax ?? 0;
    }
    else if (typeof p1 === "string" && p1.trim()) {
        const nums = p1.match(/-?\d+(\.\d+)?/g)?.map(Number) ?? [];
        targetMin = nums[0] ?? 0;
        targetMax = nums[1] ?? nums[0] ?? 0;
    }
    return {
        spend: {
            microdata: toFormString(benchmarks.spend?.microdata),
            evergreen: toFormString(benchmarks.spend?.evergreen),
            promo1st: [{ targetMin, targetMax }],
            promo2nd: toFormString(benchmarks.spend?.promo2nd),
        },
        cpoc: scalarGroup(benchmarks.cpoc),
        octr: scalarGroup(benchmarks.octr),
        roas: scalarGroup(benchmarks.roas),
        cpa: scalarGroup(benchmarks.cpa),
    };
};
// Convert the form's string-based benchmark fields into numbers (or null when
// blank) before sending to the API, since the model stores these as Numbers.
// spend.promo1st is stored as a single { targetMin, targetMax } object.
const toBenchmarkNumber = (v) => {
    if (v === "" || v === null || v === undefined)
        return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};
const toNumericBenchmarks = (data) => {
    const scalarGroup = (g) => ({
        microdata: toBenchmarkNumber(g.microdata),
        evergreen: toBenchmarkNumber(g.evergreen),
        promo1st: toBenchmarkNumber(g.promo1st),
        promo2nd: toBenchmarkNumber(g.promo2nd),
    });
    return {
        spend: {
            microdata: toBenchmarkNumber(data.spend.microdata),
            evergreen: toBenchmarkNumber(data.spend.evergreen),
            // Stored as a single object: { targetMin, targetMax }.
            promo1st: {
                targetMin: toBenchmarkNumber(data.spend.promo1st?.[0]?.targetMin) ?? 0,
                targetMax: toBenchmarkNumber(data.spend.promo1st?.[0]?.targetMax) ?? 0,
            },
            promo2nd: toBenchmarkNumber(data.spend.promo2nd),
        },
        cpoc: scalarGroup(data.cpoc),
        octr: scalarGroup(data.octr),
        roas: scalarGroup(data.roas),
        cpa: scalarGroup(data.cpa),
    };
};
const isContactEmpty = (contact) => {
    return (!contact.firstName?.trim() &&
        !contact.lastName?.trim() &&
        !contact.email?.trim());
};
// AddEditClientDrawer.tsx ke bahar (ya utils file mein)
const AVATAR_COLORS = [
    { bg: "#FEE4E2", text: "#B42318" },
    { bg: "#FEF0C7", text: "#B54708" },
    { bg: "#D1FADF", text: "#067647" },
    { bg: "#E0EAFF", text: "#3538CD" },
    { bg: "#F4EBFF", text: "#6941C6" },
    { bg: "#E0F2FE", text: "#0369A1" },
    { bg: "#FFE4E6", text: "#BE123C" },
];
function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase();
}
function getAvatarColor(index) {
    return AVATAR_COLORS[index % AVATAR_COLORS.length];
}
/* ================= COMPONENT ================= */
const AddEditClientDrawer = ({ open, onClose, mode, clientId, clientData, onSuccess, isLoading = false, status }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    /* ================= REDUX SELECTORS ================= */
    const { logsLoading, logsData, isSavingClient } = useSelector((state) => state.clientData);
    const canManageClients = useSelector(selectCanManageClients);
    const canInviteClients = useSelector(selectCanInviteClients);
    const [allBenchmarkSettings, setAllBenchmarkSettings] = useState([]);
    // Tab State
    const [activeTab, setActiveTab] = useState("client");
    // Store & Client Info
    const [storeData, setStoreData] = useState(initialStoreData);
    const [clientTypes, setClientTypes] = useState([]);
    const [clients, setClients] = useState([]);
    const [showLeadTabs, setShowLeadTabs] = useState(false);
    const [typeAnchorEl, setTypeAnchorEl] = useState(null);
    const [roleOptions, setRoleOptions] = useState([]);
    const auth = useSelector((state) => state.auth);
    const token = auth.token;
    // Contacts
    const [mainContact, setMainContact] = useState(initialContact);
    const [additionalContacts, setAdditionalContacts] = useState([]);
    const [contactsToDelete, setContactsToDelete] = useState([]);
    // Settings & Configs
    const [quickbookConfig, setQuickbookConfig] = useState(initialQuickbookConfig);
    const [settings, setSettings] = useState(initialSettings);
    const [connectedPaidMediaNetworks, setConnectedPaidMediaNetworks] = useState([]);
    // Connections (from clientData - inline now)
    const [connections, setConnections] = useState([]);
    // Benchmarks
    const [benchmarkDates, setBenchmarkDates] = useState([]);
    const [benchmarkDate, setBenchmarkDate] = useState(new Date().toISOString().split('T')[0]);
    const [benchmarkData, setBenchmarkData] = useState(initialBenchmarkData);
    const [benchmarkSettings, setBenchmarkSettings] = useState([]);
    const isExistingClientSelected = useMemo(() => {
        return Boolean(storeData.main_account_id);
    }, [storeData.main_account_id]);
    // Lead Tabs
    const [activeLeadTab, setActiveLeadTab] = useState("lead_am");
    const [leadTabUsers, setLeadTabUsers] = useState({
        lead_am: [],
        meta: [],
        search: [],
        email: [],
        affiliate: [],
        design: [],
    });
    const [loadingLeadTabs, setLoadingLeadTabs] = useState({
        lead_am: false,
        meta: false,
        search: false,
        email: false,
        affiliate: false,
        design: false,
    });
    const [accountHandlers, setAccountHandlers] = useState({
        am_id: [], assoc_am_id: [],
        paid_social_id: [], assoc_paid_social_id: [],
        paid_search_id: [], assoc_paid_search_id: [],
        design_lead_id: [], assoc_design_lead_id: [],
        email_lead_id: [], assoc_email_lead_id: [],
        affiliate_lead_id: [], assoc_affiliate_lead_id: []
    });
    const fetchRoles = useCallback(async () => {
        try {
            if (!token)
                return;
            const res = await ApiCall("GET", "/api/roles", "", { Authorization: token });
            if (res && 'data' in res && 'status' in res && res.data?.success && res?.status === 200) {
                const rolesArray = Array.isArray(res.data?.data?.roles) ? res.data.data.roles : [];
                const options = (rolesArray || []).map((r) => ({
                    value: r?._id,
                    label: r?.name || "",
                })).filter((o) => o.value && o.label);
                setRoleOptions(options);
            }
        }
        catch (e) {
            console.error("Error fetching roles:", e);
            setRoleOptions([]);
        }
    }, [token]);
    // Validation
    const [errors, setErrors] = useState({});
    const [emailErrors, setEmailErrors] = useState({});
    const [mainContactEmailError, setMainContactEmailError] = useState("");
    // ✅ Monday Calendar ID validation
    // Modals
    const [confirmInviteModalOpen, setConfirmInviteModalOpen] = useState(false);
    const [contactToInvite, setContactToInvite] = useState(null);
    const [invitingContact, setInvitingContact] = useState(null);
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [deletingContact, setDeletingContact] = useState(null);
    // File Upload
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    // Logs
    const [rows, setRows] = useState([]);
    const [logsCurrentPage, setLogsCurrentPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);
    const [logsTotalCount, setLogsTotalCount] = useState(0);
    const [availableLogDates, setAvailableLogDates] = useState([]);
    const isFirstLoad = useRef(true);
    const [logsSearchText, setLogsSearchText] = useState("");
    const [logsSortConfig, setLogsSortConfig] = useState({ columnId: null, direction: 'asc' });
    // 🔹 Month picked in CustomMonthPicker (null = no selection, "ALL_TIME" = all)
    const [logsMonthValue, setLogsMonthValue] = useState(() => {
        const now = new Date();
        return new CalendarDate(now.getFullYear(), now.getMonth() + 1, 1);
    });
    // Logs Table Config
    const [logsHiddenColumns, setLogsHiddenColumns] = useState(new Set());
    const [logsColumnVisibilityAnchor, setLogsColumnVisibilityAnchor] = useState(null);
    const [logsShowFilterRow, setLogsShowFilterRow] = useState(false);
    const [logsActiveFilterColumnId, setLogsActiveFilterColumnId] = useState(null);
    const [logsColumnFilters, setLogsColumnFilters] = useState({});
    const [logsColumnOrder, setLogsColumnOrder] = useState(null);
    const [logsPinnedColumns, setLogsPinnedColumns] = useState({ left: [], right: [] });
    /* ================= OPTIONS ================= */
    const typeOptions = [
        { value: "paid_media", label: "Paid Media" },
        { value: "affiliate", label: "Affiliate" },
        { value: "aeo", label: "AEO" },
    ];
    const paymentOptions = ["Card", "Bank Transfer", "PayPal", "ACH Bank Payments"];
    const columns = [
        { id: "date", label: "Date", sortable: true },
        { id: "type", label: "Type", sortable: true },
        { id: "status", label: "Status", sortable: true },
        { id: "accountManager", label: "AM" },
        { id: "associateAccountManager", label: "AM Ass." },
        { id: "affiliateLead", label: "OS" },
        { id: "paidSocialLead", label: "Social Lead" },
        { id: "associatePaidSocialLead", label: "Social ASS." },
        { id: "paidSearchLead", label: "Search Lead" },
        { id: "associatePaidSearchLead", label: "Search ASS." },
        { id: "emailLead", label: "Email Lead" },
        { id: "associateEmailLead", label: "Email ASS." },
        { id: "designLead", label: "Design Lead" },
        { id: "associateAffiliateLead", label: "Affiliate ASS." },
    ];
    /* ================= HELPER FUNCTIONS ================= */
    const validateEmail = useCallback((email) => {
        if (!email)
            return false;
        const emails = email.split(',').map(e => e.trim()).filter(e => e !== '');
        if (emails.length === 0)
            return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every(e => emailRegex.test(e));
    }, []);
    const formatDateForDisplay = useCallback((dateString) => {
        if (!dateString)
            return "";
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    }, []);
    const getTypeDisplayText = useCallback(() => {
        if (clientTypes.length === 0)
            return "Select Type";
        return clientTypes.map(t => typeOptions.find(o => o.value === t)?.label).join(", ");
    }, [clientTypes]);
    /* ================= API FUNCTIONS (REDUX) ================= */
    const processConnectedNetworks = useCallback((connectionsData) => {
        const networkMap = {
            meta: "Meta",
            criteo: "Criteo",
            bing: "Bing",
            adword: "AdWords",
            ga: "Google Analytics",
            klaviyo: "Klaviyo",
            shopify: "Shopify"
        };
        const networks = connectionsData.map((conn) => ({
            id: conn.network, // Changed from conn.name
            name: networkMap[conn.network] || conn.network, // Changed from conn.name
        }));
        setConnectedPaidMediaNetworks(networks);
        // Check if performance_tracking.networks already has data from API
        const existingNetworks = clientData?.clientDetails?.setting?.performance_tracking?.networks;
        // If networks data exists from API, use it; otherwise initialize from connections with false
        const newNetworksTracking = {};
        networks.forEach((network) => {
            if (existingNetworks && existingNetworks.hasOwnProperty(network.id)) {
                // Use existing value from API
                newNetworksTracking[network.id] = existingNetworks[network.id];
            }
            else {
                // Default to false for new networks from connections
                newNetworksTracking[network.id] = false;
            }
        });
        setSettings(prev => ({
            ...prev,
            performance_tracking: {
                networks: newNetworksTracking
            }
        }));
    }, [clientData]);
    // Fetch clients list using new API
    // Fetch clients list using new API
    const fetchClientsList = useCallback(async () => {
        try {
            const result = await dispatch(fetchAllClientList()).unwrap();
            if (result?.clients) {
                let opts = result.clients.map((client) => {
                    // Get main contact from client
                    const mainContact = client.client_contacts?.find((c) => c.is_main_contact)
                        || client.client_contacts?.[0];
                    return {
                        _id: client._id,
                        name: client.name,
                        email: mainContact?.email || "",
                        firstName: mainContact?.first_name || "", // ✅ Add firstName
                        lastName: mainContact?.last_name || "", // ✅ Add lastName
                        contacts: client.client_contacts || [],
                    };
                });
                if (mode === "edit" && clientId) {
                    opts = opts.filter((c) => c._id !== clientId);
                }
                // 🔥 Merge parent if editing child
                if (mode === "edit" &&
                    clientData?.clientDetails?.main_account_id &&
                    clientData?.clientDetails?.main_account) {
                    const parent = clientData.clientDetails.main_account;
                    const parentContact = clientData.mainAccountContact || clientData.mainAccount;
                    const alreadyExists = opts.some(c => c._id === parent._id);
                    if (!alreadyExists) {
                        opts.push({
                            _id: parent._id,
                            name: parent.name,
                            email: parentContact?.email || "",
                            firstName: parentContact?.first_name || "", // ✅ Add firstName
                            lastName: parentContact?.last_name || "", // ✅ Add lastName
                            contacts: parent.client_contacts || [],
                        });
                    }
                }
                setClients(opts);
            }
        }
        catch (error) {
            console.error("Error fetching clients:", error);
        }
    }, [dispatch, mode, clientId, status, clientData]);
    // Fetch logs
    const fetchLogs = useCallback(async () => {
        if (!clientId)
            return;
        try {
            const params = new URLSearchParams();
            params.append('page', logsCurrentPage.toString());
            params.append('limit', '10');
            // 🔹 Backend me query param ka naam `searchText` hai
            if (logsSearchText) {
                params.append('searchText', logsSearchText);
            }
            // 🔹 Month param "M-YYYY" format me bhejna hai
            if (logsMonthValue && logsMonthValue !== "ALL_TIME") {
                const monthParam = `${logsMonthValue.month}-${logsMonthValue.year}`; // e.g. "1-2026"
                params.append('month', monthParam);
            }
            // Agar logsMonthValue null ya "ALL_TIME" hai to month param nahi bhejna → backend "all" treat karega
            const result = await dispatch(fetchClientLogs({ clientId, params })).unwrap();
            if (result) {
                const { logsByDate, pagination } = result;
                const formattedRows = [];
                Object.entries(logsByDate || {}).forEach(([date, logs]) => {
                    logs.forEach((log) => {
                        formattedRows.push({
                            _id: log._id,
                            date: new Date(log.createdAt).toLocaleDateString(),
                            type: Array.isArray(log.type) ? log.type.join(", ") : log.type || "-",
                            status: log.status || "-",
                            accountManager: log.am_id || [],
                            associateAccountManager: log.assoc_am_id || [],
                            paidSearchLead: log.paid_search_id || [],
                            associatePaidSearchLead: log.assoc_paid_search_id || [],
                            paidSocialLead: log.paid_social_id || [],
                            associatePaidSocialLead: log.assoc_paid_social_id || [],
                            designLead: log.design_lead_id || [],
                            emailLead: log.email_lead_id || [],
                            associateEmailLead: log.assoc_email_lead_id || [],
                            affiliateLead: log.affiliate_lead_id || [],
                            associateAffiliateLead: log.assoc_affiliate_lead_id || [],
                        });
                    });
                });
                setRows(formattedRows);
                setLogsTotalPages(pagination?.totalPages || 1);
                setLogsTotalCount(pagination?.totalLogs || 0);
            }
        }
        catch (error) {
            console.error("Error fetching logs:", error);
        }
    }, [clientId, logsCurrentPage, logsSearchText, logsMonthValue, dispatch]);
    /* ================= EMAIL VALIDATION ================= */
    const handleEmailValidation = useCallback(async (field, email) => {
        if (mode === "create" && storeData.main_account_id) {
            return;
        }
        // 1) Format check
        if (email && !validateEmail(email)) {
            setEmailErrors(prev => ({ ...prev, [field]: "Please enter a valid email address." }));
            if (field === "mainContact_email")
                setMainContactEmailError("");
            return; // format galat hai → backend call mat karo
        }
        else {
            // format sahi ya empty → local format error hata do
            setEmailErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
        // 🔹 FRONTEND DUPLICATE CHECK (main + additional contacts)
        const currentEmail = email.trim().toLowerCase();
        const allEmails = [
            mainContact.email,
            ...additionalContacts.map(c => c.email)
        ]
            .filter(Boolean)
            .map(e => e.trim().toLowerCase());
        const duplicateCount = allEmails.filter(e => e === currentEmail).length;
        if (duplicateCount > 1) {
            const duplicateMsg = "This email address is already in use.";
            if (field === "mainContact_email") {
                setMainContactEmailError(duplicateMsg);
            }
            else {
                setEmailErrors(prev => ({ ...prev, [field]: duplicateMsg }));
            }
            return; // ❌ Yahin stop — backend call mat karo
        }
        // Email empty hai to exist check ki zarurat nahi
        if (!email) {
            if (field === "mainContact_email")
                setMainContactEmailError("");
            return;
        }
        // 2) Backend exist check ke liye apna _id nikalo (edit case me)
        let _id = undefined;
        if (field === "mainContact_email") {
            // Main contact
            if (mainContact.contactId) {
                _id = mainContact.contactId; // apne aap ko ignore karega
            }
        }
        else if (field.startsWith("additionalContact_email_")) {
            // Additional contact ke liye
            const idStr = field.split("_").pop() || "";
            const localId = Number(idStr);
            const contact = additionalContacts.find(c => c.id === localId);
            if (contact?.contactId) {
                _id = contact.contactId; // apne aap ko ignore karega
            }
        }
        try {
            const result = await dispatch(checkEmailExists({ email, _id })).unwrap();
            const exists = result?.exists;
            if (exists) {
                // ✅ Yahi message backend se aa raha h:
                // "This email is already in use by another contact."
                const msg = result?.message || "This email is already in use by another contact.";
                if (field === "mainContact_email") {
                    // Main contact ke email ke neeche dikhega
                    setMainContactEmailError(msg);
                }
                else {
                    // Additional contact ke neeche dikhega
                    setEmailErrors(prev => ({ ...prev, [field]: msg }));
                }
            }
            else {
                // Email available hai → koi success text nahi, sirf errors hata do
                if (field === "mainContact_email") {
                    setMainContactEmailError("");
                }
                setEmailErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field];
                    return newErrors;
                });
            }
        }
        catch (err) {
            console.error("Error checking email:", err);
            // API error pe exist error nahi dikhayenge
        }
    }, [validateEmail, mode, mainContact.contactId, additionalContacts, dispatch]);
    const handleFieldBlur = useCallback((field) => {
        if (field === "mainContact_email") {
            handleEmailValidation(field, mainContact.email);
        }
        else if (field.startsWith("additionalContact_email_")) {
            const contactId = parseInt(field.split("_").pop() || "0");
            const contact = additionalContacts.find((c) => c.id === contactId);
            if (contact) {
                handleEmailValidation(field, contact.email);
            }
        }
    }, [handleEmailValidation, mainContact.email, additionalContacts]);
    /* ================= HANDLERS ================= */
    const handleStoreDataChange = useCallback((field, value) => {
        setStoreData((prev) => ({ ...prev, [field]: value }));
        if (errors[field])
            setErrors((prev) => ({ ...prev, [field]: false }));
    }, [errors]);
    const handleMainContactChange = useCallback((field, value) => {
        setMainContact((prev) => ({ ...prev, [field]: value }));
        if (field === "firstName" && errors.mainContact_firstName)
            setErrors((prev) => ({ ...prev, mainContact_firstName: false }));
        if (field === "lastName" && errors.mainContact_lastName)
            setErrors((prev) => ({ ...prev, mainContact_lastName: false }));
        if (field === "email") {
            if (errors.mainContact_email)
                setErrors((prev) => ({ ...prev, mainContact_email: false }));
            if (emailErrors.mainContact_email)
                setEmailErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.mainContact_email;
                    return newErrors;
                });
            setMainContactEmailError("");
        }
    }, [errors, emailErrors]);
    const handleAdditionalContactChange = useCallback((id, field, value) => {
        setAdditionalContacts((prev) => prev.map((contact) => contact.id === id ? { ...contact, [field]: value } : contact));
        if (field === "email") {
            const errorKey = `additionalContact_email_${id}`;
            if (emailErrors[errorKey]) {
                setEmailErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        }
    }, [emailErrors]);
    const handleAddContact = useCallback(() => {
        setAdditionalContacts((prev) => [
            ...prev,
            {
                id: Date.now(),
                firstName: "",
                lastName: "",
                email: "",
                allowEmailNotification: false,
                status: "active",
                is_main_contact: false
            },
        ]);
    }, []);
    const handleDeleteClick = useCallback((contact) => {
        setContactToDelete(contact);
        setConfirmDeleteModalOpen(true);
    }, []);
    const handleConfirmDelete = useCallback(async () => {
        if (contactToDelete) {
            if (mode === "edit" && contactToDelete.contactId) {
                try {
                    setDeletingContact(contactToDelete.contactId);
                    await dispatch(deleteContact({ contactId: contactToDelete.contactId })).unwrap();
                    setAdditionalContacts((prev) => prev.filter((c) => c.contactId !== contactToDelete.contactId));
                }
                catch (error) {
                    console.error("Delete contact error", error);
                }
                finally {
                    setDeletingContact(null);
                }
            }
            else {
                setAdditionalContacts((prev) => prev.filter((c) => c.id !== contactToDelete.id));
            }
        }
        setConfirmDeleteModalOpen(false);
        setContactToDelete(null);
    }, [contactToDelete, mode, dispatch]);
    const handleInviteClick = useCallback((contact) => {
        setContactToInvite(contact);
        setConfirmInviteModalOpen(true);
    }, []);
    const handleConfirmInvite = useCallback(async () => {
        if (contactToInvite && contactToInvite.contactId) {
            try {
                setInvitingContact(contactToInvite.contactId);
                await dispatch(resetContactPassword({ contactId: contactToInvite.contactId })).unwrap();
            }
            catch (error) {
                console.error("Invite error", error);
            }
            finally {
                setInvitingContact(null);
            }
        }
        setConfirmInviteModalOpen(false);
        setContactToInvite(null);
    }, [contactToInvite, dispatch]);
    const handleSettingsChange = useCallback((field, value) => {
        setSettings((prev) => ({ ...prev, [field]: value }));
    }, []);
    const handleQuickbookChange = useCallback((field, value) => {
        setQuickbookConfig((prev) => ({ ...prev, [field]: value }));
    }, []);
    const handlePerformanceTrackingChange = useCallback((networkId, value) => {
        setSettings((prev) => ({
            ...prev,
            performance_tracking: {
                ...prev.performance_tracking,
                networks: {
                    ...(prev.performance_tracking?.networks || {}),
                    [networkId]: value,
                },
            },
        }));
    }, []);
    const handleBenchmarkDataChange = useCallback((category, field, value) => {
        setBenchmarkData((prev) => ({
            ...prev,
            [category]: { ...prev[category], [field]: value },
        }));
    }, []);
    const handleSpendPromo1stChange = useCallback((key, value) => {
        const num = value === "" ? 0 : Number(value);
        if (Number.isNaN(num))
            return;
        setBenchmarkData((prev) => {
            const current = Array.isArray(prev.spend.promo1st) && prev.spend.promo1st[0]
                ? prev.spend.promo1st[0]
                : { targetMin: 0, targetMax: 0 };
            return {
                ...prev,
                spend: {
                    ...prev.spend,
                    promo1st: [{ ...current, [key]: num }],
                },
            };
        });
    }, []);
    const handlePaymentToggle = (method) => (e) => {
        setQuickbookConfig((prev) => {
            const current = prev.payment || [];
            let updated;
            if (method === "ACH Bank Payments") {
                if (e.target.checked) {
                    updated = ["ACH Bank Payments"];
                }
                else {
                    updated = current.filter((item) => item !== "ACH Bank Payments");
                }
            }
            else {
                if (e.target.checked) {
                    if (current.includes("ACH Bank Payments")) {
                        updated = [method];
                    }
                    else {
                        updated = [...current, method];
                    }
                }
                else {
                    updated = current.filter((item) => item !== method);
                }
            }
            return {
                ...prev,
                payment: updated,
            };
        });
    };
    const isPaymentSelected = useCallback((method) => {
        return quickbookConfig.payment?.includes(method) || false;
    }, [quickbookConfig.payment]);
    const handleLeadTabClick = useCallback((tab) => {
        setActiveLeadTab(tab);
    }, []);
    const handleFileUpload = useCallback((e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            if (!file.type.startsWith("image/")) {
                toast.warning("Please select a valid image file");
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const MAX_WIDTH = 400;
                    const MAX_HEIGHT = 400;
                    let width = img.width;
                    let height = img.height;
                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    }
                    else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, width, height);
                        const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);
                        setStoreData((prev) => ({ ...prev, profile_pic: compressedBase64 }));
                    }
                    else {
                        setStoreData((prev) => ({ ...prev, profile_pic: event.target?.result }));
                    }
                };
                if (event.target?.result) {
                    img.src = event.target.result;
                }
            };
            reader.readAsDataURL(file);
        }
    }, [toast]);
    const handleCheckboxClick = useCallback((value, event) => {
        event.stopPropagation();
        const currentIndex = clientTypes.indexOf(value);
        const newChecked = [...clientTypes];
        if (currentIndex === -1) {
            newChecked.push(value);
        }
        else {
            newChecked.splice(currentIndex, 1);
        }
        setClientTypes(newChecked);
        if (errors.type && newChecked.length > 0) {
            setErrors(prev => ({ ...prev, type: false }));
        }
    }, [clientTypes, errors.type]);
    const handleListItemClick = useCallback((value) => {
        const currentIndex = clientTypes.indexOf(value);
        const newChecked = [...clientTypes];
        if (currentIndex === -1) {
            newChecked.push(value);
        }
        else {
            newChecked.splice(currentIndex, 1);
        }
        setClientTypes(newChecked);
        if (errors.type && newChecked.length > 0) {
            setErrors(prev => ({ ...prev, type: false }));
        }
    }, [clientTypes, errors.type]);
    const handleExistingClientChange = useCallback((value) => {
        if (value === "new") {
            setStoreData(prev => ({
                ...prev,
                is_main_account: true,
                main_account_id: null,
            }));
            setShowLeadTabs(false);
            // 🔹 Edit mode mein original contact restore karo, create mein initial
            if (mode === "edit" && clientData?.contacts) {
                const originalMainContact = clientData.contacts.find((c) => c.is_main_contact);
                if (originalMainContact) {
                    setMainContact({
                        id: Date.now(),
                        contactId: originalMainContact._id,
                        firstName: originalMainContact.first_name || "",
                        lastName: originalMainContact.last_name || "",
                        email: originalMainContact.email || "",
                        allowEmailNotification: originalMainContact.email_notification_preference || false,
                        status: originalMainContact.status || "active",
                        is_invitation_sent: originalMainContact.is_invitation_sent,
                        is_main_contact: true,
                    });
                }
            }
            else {
                setMainContact(initialContact);
            }
            return;
        }
        const selectedClient = clients.find(c => c._id === value);
        setStoreData(prev => ({
            ...prev,
            is_main_account: false,
            main_account_id: value,
        }));
        // ✅ Updated: Use firstName & lastName from selectedClient
        if (selectedClient) {
            setMainContact(prev => ({
                ...prev,
                firstName: selectedClient.firstName || "", // ✅ Use actual firstName
                lastName: selectedClient.lastName || "", // ✅ Use actual lastName
                email: selectedClient.email || "",
                // 🔹 Edit mode mein contactId hatao kyunki yeh parent ka contact hai
                contactId: undefined,
            }));
            setShowLeadTabs(true);
        }
        // 🔹 Clear any email errors when switching
        setMainContactEmailError("");
        setEmailErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.mainContact_email;
            return newErrors;
        });
    }, [clients, mode, clientData]);
    const handleLogsSearchChange = useCallback((text) => {
        setLogsSearchText(text);
        setLogsCurrentPage(1);
    }, []);
    const handleLogsSortChange = useCallback((columnId, direction) => {
        setLogsSortConfig({ columnId, direction });
    }, []);
    /* ================= FORM VALIDATION ================= */
    const validateForm = useCallback(async () => {
        const newErrors = {};
        let errorTab = null;
        // Common validations
        if (!storeData.name.trim()) {
            newErrors.name = true;
            errorTab = "client";
        }
        if (clientTypes.length === 0) {
            newErrors.type = true;
            errorTab = "client";
        }
        // ✅ Monday Calendar ID - ONLY required when NOT just "affiliate"
        // const isOnlyAffiliate = clientTypes.length === 1 && clientTypes[0] === "affiliate";
        // if (!isOnlyAffiliate && !storeData.monday_calender_id.trim()) {
        //     newErrors.monday_calender_id = true;
        //     errorTab = "client";
        // }
        if (!storeData.status) {
            newErrors.status = true;
            errorTab = "client";
        }
        // 🔹 Contact validation sirf jab existing client selected NAHI hai
        if (!isExistingClientSelected) {
            if (!mainContact.firstName.trim()) {
                newErrors.mainContact_firstName = true;
                errorTab = "client";
            }
            if (!mainContact.email.trim()) {
                newErrors.mainContact_email = true;
                errorTab = "client";
            }
            else if (!validateEmail(mainContact.email)) {
                setEmailErrors((prev) => ({ ...prev, mainContact_email: "Please enter a valid email address." }));
                return { isValid: false, errorTab: "client" };
            }
            else {
                // Backend email check
                try {
                    const _id = mode === "edit" && mainContact.contactId
                        ? mainContact.contactId
                        : undefined;
                    const result = await dispatch(checkEmailExists({ email: mainContact.email, _id })).unwrap();
                    if (result?.exists) {
                        setMainContactEmailError(result?.message || "This email is already associated with an existing contact.");
                        return { isValid: false, errorTab: "client" };
                    }
                }
                catch (err) {
                    console.error("Error checking main contact email in validateForm:", err);
                }
            }
            // 🔹 Validate only NON-EMPTY additional contacts
            const filledContacts = additionalContacts.filter(c => !isContactEmpty(c));
            for (const contact of filledContacts) {
                if (!contact.firstName.trim()) {
                    newErrors[`additionalContact_firstName_${contact.id}`] = true;
                    errorTab = "contacts";
                }
                if (!contact.email.trim()) {
                    newErrors[`additionalContact_email_${contact.id}`] = true;
                    errorTab = "contacts";
                }
                else if (!validateEmail(contact.email)) {
                    setEmailErrors((prev) => ({
                        ...prev,
                        [`additionalContact_email_${contact.id}`]: "Please enter a valid email address."
                    }));
                    return { isValid: false, errorTab: "contacts" };
                }
            }
        }
        setErrors(newErrors);
        const isValid = Object.keys(newErrors).length === 0 && !mainContactEmailError;
        if (!isValid && !errorTab && mainContactEmailError) {
            errorTab = "client";
        }
        return { isValid, errorTab };
    }, [
        storeData,
        clientTypes,
        mainContact,
        additionalContacts,
        validateEmail,
        mode,
        mainContactEmailError,
        dispatch,
        isExistingClientSelected
    ]);
    /* ================= SAVE HANDLER ================= */
    const isSaveDisabled = useMemo(() => {
        if (mode === "edit")
            return false;
        if (!storeData.name.trim())
            return true;
        // 2. Type required
        if (clientTypes.length === 0)
            return true;
        // 3. Monday Calendar ID - required when NOT only affiliate
        // const isOnlyAffiliate = clientTypes.length === 1 && clientTypes[0] === "affiliate";
        // if (!isOnlyAffiliate && !storeData.monday_calender_id.trim()) return true;
        // 4. Main Contact validation - only when existing client NOT selected
        if (!isExistingClientSelected) {
            if (!mainContact.firstName.trim())
                return true;
            // if (!mainContact.lastName.trim()) return true;
            if (!mainContact.email.trim())
                return true;
            if (!validateEmail(mainContact.email))
                return true;
            if (mainContactEmailError)
                return true;
        }
        // if (mode === "edit") {
        //     if (!quickbookConfig.display_name.trim()) return true;
        //     if (!quickbookConfig.first_name.trim()) return true;
        //     if (!quickbookConfig.last_name.trim()) return true;
        //     if (!quickbookConfig.email.trim()) return true;
        //     if (quickbookConfig.email && !validateEmail(quickbookConfig.email)) return true;
        // }
        return false;
    }, [
        mode,
        storeData.name,
        // storeData.monday_calender_id,
        clientTypes,
        mainContact.firstName,
        mainContact.lastName,
        mainContact.email,
        mainContactEmailError,
        validateEmail,
        isExistingClientSelected
    ]);
    const handleSave = useCallback(async () => {
        const { isValid, errorTab } = await validateForm();
        if (!isValid) {
            toast.warning("Please fill in all required fields.");
            if (errorTab) {
                setActiveTab(errorTab);
                // Wait for the active tab to render, then scroll to the first error field
                setTimeout(() => {
                    const firstError = document.querySelector('.input-error, .error-message, [aria-invalid="true"]');
                    if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Try to focus the input inside if possible
                        const input = firstError.querySelector('input') || firstError;
                        if (input && typeof input.focus === 'function') {
                            input.focus();
                        }
                    }
                }, 150);
            }
            return;
        }
        try {
            // 🔹 Filter out empty additional contacts
            const validAdditionalContacts = additionalContacts.filter(contact => !isContactEmpty(contact));
            let allContactsData = [];
            // 🔹 Main contact sirf tab include karo jab existing client NAHI hai
            if (!isExistingClientSelected) {
                let mainContactData = {
                    is_main_contact: true,
                    first_name: mainContact.firstName,
                    last_name: mainContact.lastName,
                    email: mainContact.email,
                    email_notification_preference: mainContact.allowEmailNotification,
                    status: mainContact.status || "active",
                };
                if (mode === "create") {
                    mainContactData.password = `${mainContact.firstName || "User"}@123`;
                }
                if (mainContact.contactId) {
                    mainContactData.contactId = mainContact.contactId;
                }
                allContactsData.push(mainContactData);
            }
            // 🔹 Additional contacts HAMESHA include karo (edit mode mein)
            const additionalContactsData = validAdditionalContacts.map((contact) => {
                const contactData = {
                    is_main_contact: false,
                    first_name: contact.firstName,
                    last_name: contact.lastName,
                    email: contact.email,
                    email_notification_preference: contact.allowEmailNotification,
                    status: contact.status || "active",
                };
                if (mode === "create") {
                    contactData.password = `${contact.firstName || "User"}@123`;
                }
                if (contact.contactId) {
                    contactData.contactId = contact.contactId;
                }
                return contactData;
            });
            // ✅ Additional contacts add karo
            allContactsData = [...allContactsData, ...additionalContactsData];
            // 🔹 Request data banao
            // Performance Tracking is a paid-media-only feature and its UI is
            // hidden for affiliate-only clients. For those clients send an empty
            // networks map so no stale toggles are persisted (the backend stores
            // this as an empty Map, so it never throws).
            const isPaidMediaClient = clientTypes.includes("paid_media");
            const requestData = {
                name: storeData.name,
                type: clientTypes,
                accountHandlers,
                setting: {
                    ...settings,
                    performance_tracking: {
                        networks: isPaidMediaClient
                            ? (settings.performance_tracking?.networks || {})
                            : {}
                    }
                },
                quickbook_configuration: quickbookConfig,
                status: storeData.status,
                monday_calender_id: storeData.monday_calender_id,
                is_main_account: storeData.is_main_account,
                main_account_id: storeData.main_account_id,
                role_id: storeData.role_id,
                profile_pic: storeData.profile_pic || null,
            };
            if (mode === "edit") {
                requestData.benchmarkSettings = benchmarkSettings?.length > 0
                    ? benchmarkSettings
                    : { benchmarks: toNumericBenchmarks(benchmarkData) };
            }
            // ✅ Contacts HAMESHA add karo (agar koi hai)
            if (allContactsData.length > 0) {
                requestData.contacts = allContactsData;
            }
            // ✅ Calculate excluded parent contacts for child clients
            if (isExistingClientSelected) {
                const parentClient = clients.find(c => c._id === storeData.main_account_id);
                if (parentClient) {
                    const parentContacts = (parentClient.contacts || []).filter((c) => !c.is_main_contact);
                    const excludedIds = parentContacts
                        .filter((pc) => !validAdditionalContacts.some(vc => vc.contactId === pc._id))
                        .map((pc) => pc._id);
                    requestData.excluded_parent_contacts = excludedIds;
                }
            }
            // Dispatch create or update action
            if (mode === "edit" && clientId) {
                await dispatch(updateClient({ clientId, data: requestData })).unwrap();
            }
            else {
                await dispatch(createClient({ data: requestData })).unwrap();
            }
            onSuccess?.();
            onClose();
        }
        catch (error) {
            console.error("Save error:", error);
        }
    }, [
        validateForm,
        toast,
        isExistingClientSelected,
        mainContact,
        additionalContacts,
        storeData,
        clientTypes,
        accountHandlers,
        benchmarkSettings,
        benchmarkData,
        settings,
        quickbookConfig,
        mode,
        clientId,
        dispatch,
        onSuccess,
        onClose
    ]);
    /* ================= LOAD CLIENT DATA ================= */
    const loadClientData = useCallback((data) => {
        try {
            const client = data;
            let clientTypesArray = [];
            if (client.clientDetails?.type) {
                if (Array.isArray(client.clientDetails.type)) {
                    clientTypesArray = client.clientDetails.type.map((t) => typeof t === "string" ? t.trim().toLowerCase() : String(t).toLowerCase());
                }
                else if (typeof client.clientDetails.type === "string") {
                    clientTypesArray = client.clientDetails.type
                        .split(",")
                        .map((t) => t.trim().toLowerCase());
                }
            }
            setStoreData((prev) => ({
                ...prev,
                name: client.clientDetails?.name || "",
                type: clientTypesArray,
                status: client.clientDetails?.status || "",
                profile_pic: client.clientDetails?.profile_pic || null,
                start_date: client.clientDetails?.start_date
                    ? new Date(client.clientDetails.start_date).toISOString().split("T")[0]
                    : "",
                termination_date: client.clientDetails?.termination_date || "",
                website_url: client.clientDetails?.website_url || "",
                monday_calender_id: client.clientDetails?.monday_calender_id || "",
                monday_board_id: client.clientDetails?.monday_board_id || "",
                is_main_account: client.clientDetails?.is_main_account ?? false,
                main_account_id: typeof client.clientDetails?.main_account_id === "object"
                    ? client.clientDetails?.main_account_id?._id
                    : client.clientDetails?.main_account_id || null,
                role_id: (client.clientDetails?.role_id?._id || client.clientDetails?.role_id || null),
            }));
            setClientTypes(clientTypesArray);
            // Load connections from clientData (now inline)
            if (client.connections && Array.isArray(client.connections)) {
                setConnections(client.connections);
                processConnectedNetworks(client.connections);
            }
            // Load QuickBook configuration
            if (client.clientDetails?.quickbook_configuration) {
                const qbConfig = client.clientDetails.quickbook_configuration;
                let paymentArray = [];
                if (qbConfig.payment) {
                    if (Array.isArray(qbConfig.payment)) {
                        paymentArray = qbConfig.payment;
                    }
                    else if (typeof qbConfig.payment === 'string') {
                        paymentArray = qbConfig.payment.split(',').map((p) => p.trim()).filter(Boolean);
                    }
                }
                setQuickbookConfig({
                    display_name: qbConfig.display_name || "",
                    company_name: qbConfig.company_name || "",
                    first_name: qbConfig.first_name || "",
                    last_name: qbConfig.last_name || "",
                    email: qbConfig.email || "",
                    internal_customer_notes: qbConfig.internal_customer_notes || "",
                    street_address_first: qbConfig.street_address_first || "",
                    street_address_second: qbConfig.street_address_second || "",
                    city: qbConfig.city || "",
                    state: qbConfig.state || "",
                    zipcode: qbConfig.zipcode || "",
                    country: qbConfig.country || "",
                    payment: paymentArray,
                });
            }
            // Load account handlers for lead tabs
            if (client.accountHandlers) {
                const handlers = client.accountHandlers;
                const mapUsers = (arr1 = [], arr2 = []) => {
                    const combined = [...arr1, ...arr2];
                    return Array.from(new Map(combined.map(u => [u._id, u])).values());
                };
                setLeadTabUsers({
                    lead_am: handlers.am_id || [],
                    assoc_lead_am: handlers.assoc_am_id || [],
                    meta: handlers.paid_social_id || [],
                    assoc_meta: handlers.assoc_paid_social_id || [],
                    search: handlers.paid_search_id || [],
                    assoc_search: handlers.assoc_paid_search_id || [],
                    email: handlers.email_lead_id || [],
                    assoc_email: handlers.assoc_email_lead_id || [],
                    affiliate: handlers.affiliate_lead_id || [],
                    assoc_affiliate: handlers.assoc_affiliate_lead_id || [],
                    design: handlers.design_lead_id || [],
                    assoc_design: handlers.assoc_design_lead_id || [],
                });
                setAccountHandlers({
                    am_id: handlers.am_id || [],
                    assoc_am_id: handlers.assoc_am_id || [],
                    paid_social_id: handlers.paid_social_id || [],
                    assoc_paid_social_id: handlers.assoc_paid_social_id || [],
                    paid_search_id: handlers.paid_search_id || [],
                    assoc_paid_search_id: handlers.assoc_paid_search_id || [],
                    design_lead_id: handlers.design_lead_id || [],
                    assoc_design_lead_id: handlers.assoc_design_lead_id || [],
                    email_lead_id: handlers.email_lead_id || [],
                    assoc_email_lead_id: handlers.assoc_email_lead_id || [],
                    affiliate_lead_id: handlers.affiliate_lead_id || [],
                    assoc_affiliate_lead_id: handlers.assoc_affiliate_lead_id || [],
                });
            }
            // Load settings
            if (client.clientDetails?.setting) {
                const clientSettings = client.clientDetails.setting;
                // Get networks from performance_tracking
                let networksTracking = {};
                if (clientSettings.performance_tracking?.networks) {
                    networksTracking = { ...clientSettings.performance_tracking.networks };
                }
                setSettings({
                    shopify_filter: clientSettings.shopify_filter || [],
                    combined_bench_mark: clientSettings.combined_bench_mark || false,
                    include_cpaIn_ad_testing: clientSettings.include_cpaIn_ad_testing || false,
                    performance_tracking: {
                        networks: networksTracking
                    },
                    shopify_revenue_settings: {
                        account_summary: clientSettings.shopify_revenue_settings?.account_summary || "G-D+S+T",
                        performance_tracker: clientSettings.shopify_revenue_settings?.performance_tracker || "G-D+S+T",
                        client_report: clientSettings.shopify_revenue_settings?.client_report || "G-D+S+T",
                        divergence_report: clientSettings.shopify_revenue_settings?.divergence_report || "G-D+S+T",
                        commission_tracker: clientSettings.shopify_revenue_settings?.commission_tracker || "netsales",
                    },
                });
            }
            // Load contacts
            if (client.contacts && Array.isArray(client.contacts)) {
                const mainContactData = client.contacts.find((contact) => contact.is_main_contact);
                if (mainContactData) {
                    setMainContact({
                        id: Date.now(),
                        contactId: mainContactData._id,
                        firstName: mainContactData.first_name || "",
                        lastName: mainContactData.last_name || "",
                        email: mainContactData.email || "",
                        allowEmailNotification: mainContactData.email_notification_preference === true,
                        status: mainContactData.status || "active",
                        is_invitation_sent: mainContactData.is_invitation_sent,
                        is_main_contact: true,
                    });
                }
                const otherContacts = client.contacts
                    .filter((contact) => !contact.is_main_contact)
                    .map((contact, index) => ({
                    id: Date.now() + index + 1,
                    contactId: contact._id,
                    firstName: contact.first_name || "",
                    lastName: contact.last_name || "",
                    email: contact.email || "",
                    allowEmailNotification: contact.email_notification_preference === true,
                    status: contact.status || "active",
                    is_invitation_sent: contact.is_invitation_sent,
                    is_main_contact: false,
                }));
                setAdditionalContacts(otherContacts);
            }
            // 🔹 If child client (has main_account_id) → use mainAccountContact
            const parentContactData = client.mainAccountContact || client.mainAccount;
            if (client.clientDetails?.main_account_id && parentContactData && Object.keys(parentContactData).length > 0) {
                const parentContact = parentContactData;
                setMainContact({
                    id: Date.now(),
                    firstName: parentContact.first_name || "",
                    lastName: parentContact.last_name || "",
                    email: parentContact.email || "",
                    allowEmailNotification: false,
                    status: "active",
                    is_main_contact: true,
                });
            }
            // Set show lead tabs based on account type
            if (client.clientDetails?.is_main_account === false && client.clientDetails?.main_account_id) {
                setShowLeadTabs(true);
            }
            else if (client.clientDetails?.is_main_account === true) {
                setShowLeadTabs(false);
            }
            if (client.benchmarkSettings && client.benchmarkSettings.length > 0) {
                setAllBenchmarkSettings(client.benchmarkSettings);
                const dates = client.benchmarkSettings.map((b) => b.date);
                setBenchmarkDates(dates);
                const validDates = dates.filter((d) => d !== "all");
                const latest = validDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
                setBenchmarkDate(latest);
                const latestData = client.benchmarkSettings.find((b) => b.date === latest);
                if (latestData?.benchmarks) {
                    setBenchmarkData(normalizeBenchmarks(latestData.benchmarks));
                }
            }
        }
        catch (error) {
            console.error("Error loading client data:", error);
            toast.warning("Failed to load client data. Please try again.");
        }
    }, [toast, processConnectedNetworks]);
    /* ================= RESET STATE ================= */
    const isDateEditable = useCallback((date) => {
        if (mode === "create")
            return true;
        const validDates = benchmarkDates.filter(d => d !== "all");
        if (validDates.length === 0)
            return true;
        const latest = validDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
        const today = new Date().toISOString().split("T")[0];
        if (date === today)
            return true;
        return date === latest;
    }, [benchmarkDates, mode]);
    useEffect(() => {
        if (!benchmarkDate || !allBenchmarkSettings.length)
            return;
        const selected = allBenchmarkSettings.find((b) => b.date === benchmarkDate);
        if (selected?.benchmarks) {
            setBenchmarkData(normalizeBenchmarks(selected.benchmarks));
        }
    }, [benchmarkDate, allBenchmarkSettings]);
    const resetState = useCallback(() => {
        setActiveTab("client");
        setStoreData(initialStoreData);
        setClientTypes([]);
        setShowLeadTabs(false);
        setMainContact(initialContact);
        setAdditionalContacts([]);
        setQuickbookConfig(initialQuickbookConfig);
        setSettings(initialSettings);
        setBenchmarkData(initialBenchmarkData);
        setBenchmarkDate(new Date().toISOString().split('T')[0]);
        setBenchmarkDates([]);
        setAllBenchmarkSettings([]);
        setConnections([]);
        setConnectedPaidMediaNetworks([]);
        setAccountHandlers({
            am_id: [], assoc_am_id: [],
            paid_social_id: [], assoc_paid_social_id: [],
            paid_search_id: [], assoc_paid_search_id: [],
            design_lead_id: [], assoc_design_lead_id: [],
            email_lead_id: [], assoc_email_lead_id: [],
            affiliate_lead_id: [], assoc_affiliate_lead_id: []
        });
        setLeadTabUsers({
            lead_am: [],
            meta: [],
            search: [],
            email: [],
            affiliate: [],
            design: [],
        });
        setErrors({});
        setEmailErrors({});
        setMainContactEmailError("");
        setRows([]);
        setLogsCurrentPage(1);
        setLogsSearchText("");
    }, []);
    /* ================= EFFECTS ================= */
    // Load client data when editing
    useEffect(() => {
        if (clientData && mode === "edit") {
            loadClientData(clientData);
        }
    }, [clientData, mode, loadClientData]);
    // Fetch data when drawer opens
    useEffect(() => {
        if (open) {
            fetchClientsList();
            fetchRoles();
        }
        else {
            resetState();
        }
    }, [open, fetchClientsList, resetState]);
    // Fetch logs when on logs tab
    useEffect(() => {
        if (mode === "edit" && activeTab === "logs" && clientId) {
            fetchLogs();
        }
    }, [mode, activeTab, clientId, logsCurrentPage, logsSearchText, fetchLogs]);
    /* ================= RENDER CELL FOR LOGS ================= */
    // Component ke andar
    const overlayHover = useOverlayHoverHandlers();
    const renderCell = useCallback((row, col) => {
        const columnId = col.id;
        const cellValue = row?.[columnId];
        // 🔹 Handle Type column
        if (columnId === "type") {
            const rawType = cellValue;
            if (!rawType)
                return "-";
            const types = Array.isArray(rawType) ? rawType : rawType.split(",");
            return (_jsx("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap" }, children: types.map((t, index) => {
                    const formatted = t
                        .trim()
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, c => c.toUpperCase());
                    return (_jsx("span", { style: {
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontWeight: 500,
                            background: "#e3f2fd",
                            color: "#1976d2",
                        }, className: "all-Font-Sizes", children: formatted }, index));
                }) }));
        }
        // 🔹 Handle Status column
        if (columnId === "status") {
            const isActive = cellValue?.toLowerCase() === "active";
            return (_jsxs("span", { className: `status-badge ${isActive ? "active" : "inactive"}`, children: [_jsx("span", { className: "status-dot" }), isActive ? "Active" : "In-Active"] }));
        }
        // 🔹 Handle User Array columns with HOVER EFFECT
        const userArrayColumns = [
            "accountManager",
            "associateAccountManager",
            "paidSearchLead",
            "associatePaidSearchLead",
            "paidSocialLead",
            "associatePaidSocialLead",
            "designLead",
            "emailLead",
            "associateEmailLead",
            "affiliateLead",
            "associateAffiliateLead"
        ];
        if (userArrayColumns.includes(columnId)) {
            const users = Array.isArray(cellValue) ? cellValue : [];
            if (users.length === 0)
                return _jsx("span", { style: { color: "#9CA3AF" }, children: "\u2014" });
            const visibleUsers = users.slice(0, 4);
            const extraUsers = users.slice(4);
            return (_jsxs("div", { style: { display: "flex", alignItems: "center", position: "relative" }, children: [visibleUsers.map((user, idx) => {
                        const color = getAvatarColor(idx);
                        const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                        return (_jsx(HoverTooltip, { title: fullName || "Unknown", arrow: true, placement: "top", PopperProps: { sx: { zIndex: 1400 } }, children: _jsx("span", { ...overlayHover, style: {
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    background: color.bg,
                                    color: color.text,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    marginLeft: idx === 0 ? 0 : -8,
                                    border: "2px solid #fff",
                                    cursor: "pointer",
                                    zIndex: visibleUsers.length - idx,
                                }, children: getInitials(fullName) }) }, user?._id || idx));
                    }), extraUsers.length > 0 && (_jsx(HoverTooltip, { title: _jsx(Box, { sx: {
                                background: "black",
                                borderRadius: "8px",
                                overflow: "hidden",
                                minWidth: 180,
                            }, children: extraUsers.map((user, idx) => (_jsx(Box, { sx: {
                                    px: 2,
                                    py: 1.2,
                                    fontSize: "14px",
                                    textTransform: "capitalize",
                                    color: "#fff",
                                    borderBottom: idx !== extraUsers.length - 1
                                        ? "1px solid rgba(255, 255, 255, 0.5)"
                                        : "none",
                                }, children: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Unknown" }, user?._id || idx))) }), arrow: true, placement: "top", PopperProps: {
                            sx: {
                                zIndex: 1400,
                                "& .MuiTooltip-tooltip": {
                                    background: "transparent",
                                    padding: 0,
                                },
                            },
                        }, children: _jsxs("span", { ...overlayHover, style: {
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: "#F3F4F6",
                                color: "#374151",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: 600,
                                marginLeft: -8,
                                border: "2px solid #fff",
                                cursor: "pointer",
                            }, children: ["+", extraUsers.length] }) }))] }));
        }
        // 🔹 Handle Date column
        if (columnId === "date") {
            return _jsx("span", { children: cellValue || "-" });
        }
        // 🔹 Safety check
        if (cellValue === null || cellValue === undefined) {
            return _jsx("span", { children: "-" });
        }
        if (typeof cellValue === "object") {
            console.warn(`⚠️ Unexpected object in column "${columnId}":`, cellValue);
            return _jsx("span", { children: "-" });
        }
        // 🔹 Default
        return _jsx("span", { children: String(cellValue) });
    }, [overlayHover]); // ✅ Add overlayHover as dependency
    /* ================= DERIVED STATE ================= */
    const isDataLoading = mode === "edit" && !clientData && isLoading;
    const loading = isSavingClient;
    // Whether Meta is one of the client's connected networks.
    const hasMetaConnected = useMemo(() => connectedPaidMediaNetworks?.some((n) => n.id === "meta") ?? false, [connectedPaidMediaNetworks]);
    // Settings tab is available for paid-media clients, and additionally for
    // affiliate clients that have Meta connected (so they can access the Meta
    // Ad Testing settings). Performance Tracking inside the tab remains
    // paid-media only (see SettingsTab).
    const showSettingsTab = useMemo(() => mode === "edit" &&
        (clientTypes.includes("paid_media") ||
            (clientTypes.includes("affiliate") && hasMetaConnected)), [mode, clientTypes, hasMetaConnected]);
    const processedRows = useMemo(() => {
        let updatedRows = [...rows];
        // 🔹 Filtering
        Object.entries(logsColumnFilters).forEach(([columnId, value]) => {
            if (!value)
                return;
            updatedRows = updatedRows.filter((row) => {
                const cellValue = row[columnId];
                if (Array.isArray(cellValue)) {
                    return cellValue.some((v) => `${v.first_name || ""} ${v.last_name || ""}`
                        .toLowerCase()
                        .includes(value.toLowerCase()));
                }
                return String(cellValue)
                    .toLowerCase()
                    .includes(value.toLowerCase());
            });
        });
        // 🔹 Sorting
        if (logsSortConfig.columnId) {
            updatedRows.sort((a, b) => {
                const aVal = a[logsSortConfig.columnId];
                const bVal = b[logsSortConfig.columnId];
                const aString = Array.isArray(aVal)
                    ? aVal.length
                    : String(aVal || "");
                const bString = Array.isArray(bVal)
                    ? bVal.length
                    : String(bVal || "");
                if (logsSortConfig.direction === "asc") {
                    return aString > bString ? 1 : -1;
                }
                else {
                    return aString < bString ? 1 : -1;
                }
            });
        }
        return updatedRows;
    }, [rows, logsColumnFilters, logsSortConfig]);
    /* ================= RENDER ================= */
    if (!open)
        return null;
    const headerActions = canManageClients ? (_jsx(AppButton, { variant: "contained", color: "primary", startIcon: _jsx(SaveIcon, {}), onClick: handleSave, disabled: loading || isDataLoading || isSaveDisabled, children: loading ? "Saving..." : "Save" })) : null;
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: mode === "edit"
                    ? `Edit ${clientData?.clientDetails?.name ?? ""}`
                    : "Add New Client", headerActions: headerActions, isLoading: isDataLoading, width: "md" // ✅ Large width for client drawer
                , className: "client-drawer" // ✅ Custom class agar chahiye
                , children: _jsxs("div", { className: "inner-body", children: [_jsx("div", { className: "outertab", children: _jsxs("div", { className: "drawer-tabs", children: [_jsx("button", { className: `tab ${activeTab === "client" ? "active" : ""} all-Font-Sizes-12`, onClick: () => setActiveTab("client"), disabled: isDataLoading, children: "Client Information" }), _jsx("button", { className: `tab ${activeTab === "contacts" ? "active" : ""} all-Font-Sizes-12`, onClick: () => setActiveTab("contacts"), disabled: isDataLoading, children: "Multiple Contacts" }), showSettingsTab && (_jsx("button", { className: `tab ${activeTab === "settings" ? "active" : ""} all-Font-Sizes-12`, onClick: () => setActiveTab("settings"), disabled: isDataLoading, children: "Settings" })), _jsx("button", { className: `tab ${activeTab === "quickbook" ? "active" : ""} all-Font-Sizes-12`, onClick: () => setActiveTab("quickbook"), disabled: isDataLoading, children: "Quick Book" }), mode === "edit" && (_jsx("button", { className: `tab ${activeTab === "logs" ? "active" : ""} all-Font-Sizes-12`, onClick: () => setActiveTab("logs"), disabled: isDataLoading, children: "Change Logs" }))] }) }), _jsx("div", { className: "drawer-content", children: loading || isDataLoading ? (_jsx("div", { className: "loading-spinner", children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [activeTab === "client" && (_jsx(ClientInfoTab, { storeData: storeData, handleStoreDataChange: handleStoreDataChange, errors: errors, fileInputRef: fileInputRef, handleFileUpload: handleFileUpload, mainContact: mainContact, handleMainContactChange: handleMainContactChange, handleFieldBlur: handleFieldBlur, emailErrors: emailErrors, mainContactEmailError: mainContactEmailError, clientTypes: clientTypes, typeAnchorEl: typeAnchorEl, setTypeAnchorEl: setTypeAnchorEl, getTypeDisplayText: getTypeDisplayText, typeOptions: typeOptions, handleListItemClick: handleListItemClick, handleCheckboxClick: handleCheckboxClick, clients: clients, handleExistingClientChange: handleExistingClientChange, mode: mode, activeLeadTab: activeLeadTab, handleLeadTabClick: handleLeadTabClick, loadingLeadTabs: loadingLeadTabs, leadTabUsers: leadTabUsers, showLeadTabs: showLeadTabs, isExistingClientSelected: isExistingClientSelected, setErrors: setErrors, setClientTypes: setClientTypes, canManageClients: canManageClients, roleOptions: roleOptions })), activeTab === "contacts" && (_jsx(ContactsTab, { additionalContacts: additionalContacts, handleAddContact: handleAddContact, handleInviteClick: handleInviteClick, handleDeleteClick: handleDeleteClick, handleAdditionalContactChange: handleAdditionalContactChange, handleFieldBlur: handleFieldBlur, emailErrors: emailErrors, errors: errors, mainContactEmailError: mainContactEmailError, mode: mode, invitingContact: invitingContact, deletingContact: deletingContact, canManageClients: canManageClients, canInviteClients: canInviteClients, isExistingClientSelected: isExistingClientSelected, selectedClientContacts: isExistingClientSelected ? (clients.find(c => c._id === storeData.main_account_id)?.contacts || []) : [], setAdditionalContacts: setAdditionalContacts })), activeTab === "settings" && (_jsx(SettingsTab, { settings: settings, handleSettingsChange: handleSettingsChange, benchmarkDate: benchmarkDate, setBenchmarkDate: setBenchmarkDate, mode: mode, clientId: clientId || null, fetchBenchmarkDataForDate: () => { }, benchmarkDates: benchmarkDates, formatDateForDisplay: formatDateForDisplay, benchmarkData: benchmarkData, handleBenchmarkDataChange: handleBenchmarkDataChange, handleSpendPromo1stChange: handleSpendPromo1stChange, isDateEditable: isDateEditable, clientTypes: clientTypes, clientData: clientData, connectedPaidMediaNetworks: connectedPaidMediaNetworks, handlePerformanceTrackingChange: handlePerformanceTrackingChange, canManageClients: canManageClients })), activeTab === "quickbook" && (_jsx(QuickBookTab, { quickbookConfig: quickbookConfig, handleQuickbookChange: handleQuickbookChange, paymentOptions: paymentOptions, isPaymentSelected: isPaymentSelected, handlePaymentToggle: handlePaymentToggle, canManageClients: canManageClients })), activeTab === "logs" && (_jsx(ChangeLogsTab, { logsMonthValue: logsMonthValue, setLogsMonthValue: setLogsMonthValue, setLogsCurrentPage: setLogsCurrentPage, handleLogsSearchChange: handleLogsSearchChange, logsSearchText: logsSearchText, handleLogsColumnVisibilityClick: (e) => setLogsColumnVisibilityAnchor(e.currentTarget), setLogsShowFilterRow: setLogsShowFilterRow, setLogsActiveFilterColumnId: setLogsActiveFilterColumnId, columns: columns, rows: processedRows, renderCell: renderCell, logsSortConfig: logsSortConfig, handleLogsSortChange: handleLogsSortChange, loadingLogs: logsLoading, isFirstLoad: isFirstLoad.current, handleLogsSelectionChange: () => { }, logsHiddenColumns: logsHiddenColumns, handleLogsColumnVisibilityChange: setLogsHiddenColumns, logsColumnVisibilityAnchor: logsColumnVisibilityAnchor, handleLogsColumnVisibilityClose: () => setLogsColumnVisibilityAnchor(null), logsShowFilterRow: logsShowFilterRow, logsActiveFilterColumnId: logsActiveFilterColumnId, logsColumnFilters: logsColumnFilters, handleLogsColumnFilterChange: (id, val) => setLogsColumnFilters(prev => ({ ...prev, [id]: val })), handleLogsFilterRowToggle: () => { }, logsColumnOrder: logsColumnOrder, handleLogsColumnOrderChange: setLogsColumnOrder, logsPinnedColumns: logsPinnedColumns, handleLogsColumnPinningChange: setLogsPinnedColumns, logsColumnFilterConfig: {}, logsTotalPages: logsTotalPages, logsCurrentPage: logsCurrentPage, handleLogsPageChange: setLogsCurrentPage, logsTotalCount: logsTotalCount }))] })) })] }) }), _jsx(ConfirmActionModal, { open: confirmInviteModalOpen, title: contactToInvite?.is_invitation_sent ? "Resend Invitation" : "Send Invitation", isDeleteIcon: false, isConfirm: true, description: `Are you sure you want to ${contactToInvite?.is_invitation_sent ? 're-send' : 'send'} an invitation to ${contactToInvite?.firstName} ${contactToInvite?.lastName}? A new password will be generated and sent to ${contactToInvite?.email}.`, confirmText: contactToInvite?.is_invitation_sent ? "Resend Invitation" : "Send Invitation", cancelText: "Cancel", loading: !!invitingContact, onClose: () => {
                    setConfirmInviteModalOpen(false);
                    setContactToInvite(null);
                }, onConfirm: handleConfirmInvite }), _jsx(ConfirmActionModal, { open: confirmDeleteModalOpen, title: mode === "edit" ? "Delete Contact" : "Remove Contact", isDeleteIcon: mode === "edit", isConfirm: mode !== "edit", description: mode === "edit"
                    ? `Are you sure you want to delete contact ${contactToDelete?.firstName} ${contactToDelete?.lastName}? This action cannot be undone and all contact data will be lost.`
                    : `Are you sure you want to remove contact ${contactToDelete?.firstName} ${contactToDelete?.lastName}?`, confirmText: mode === "edit" ? "Delete Contact" : "Remove Contact", cancelText: "Cancel", loading: !!deletingContact, onClose: () => {
                    setConfirmDeleteModalOpen(false);
                    setContactToDelete(null);
                }, onConfirm: handleConfirmDelete })] }));
};
export default AddEditClientDrawer;
